/**
 * Parse mm:ss or hh:mm:ss timestamp input to seconds.
 */
export function parseTimestampInput(value, label = 'Marca de tiempo') {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) throw new Error(`${label} es obligatorio`);

  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const num = Number(trimmed);
    if (Number.isNaN(num) || num < 0) throw new Error(`${label} inválido`);
    return num;
  }

  const parts = trimmed.split(':').map((part) => part.trim());
  if (parts.length < 2 || parts.length > 3) {
    throw new Error(`${label} debe ser mm:ss o hh:mm:ss`);
  }

  const nums = parts.map((part) => {
    if (!/^\d+$/.test(part)) throw new Error(`${label} inválido`);
    return Number(part);
  });

  let seconds;
  if (parts.length === 2) {
    const [minutes, secs] = nums;
    if (secs >= 60) throw new Error(`${label}: los segundos deben ser menores a 60`);
    seconds = minutes * 60 + secs;
  } else {
    const [hours, minutes, secs] = nums;
    if (minutes >= 60 || secs >= 60) throw new Error(`${label} inválido`);
    seconds = hours * 3600 + minutes * 60 + secs;
  }

  if (seconds < 0) throw new Error(`${label} inválido`);
  return seconds;
}

/** Format seconds as mm:ss, or hh:mm:ss when >= 1 hour. */
export function formatTimestamp(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

export function normalizeChapters(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((chapter) => chapter && typeof chapter.title === 'string' && chapter.title.trim())
    .map((chapter) => ({
      startSeconds: Number(chapter.start_seconds ?? chapter.startSeconds ?? 0),
      title: chapter.title.trim(),
    }))
    .sort((a, b) => a.startSeconds - b.startSeconds);
}

export function chaptersToDb(chapters) {
  return chapters.map((chapter) => ({
    start_seconds: chapter.startSeconds,
    title: chapter.title,
  }));
}

export function chaptersFromForm(rows) {
  const parsed = [];
  for (const row of rows) {
    if (!row.timestamp?.trim() && !row.title?.trim()) continue;
    const startSeconds = parseTimestampInput(row.timestamp, 'Marca de tiempo');
    if (!row.title?.trim()) throw new Error('Cada capítulo necesita un título');
    parsed.push({ startSeconds, title: row.title.trim() });
  }
  return parsed.sort((a, b) => a.startSeconds - b.startSeconds);
}

/**
 * Draft-safe chapter parse: keep only complete rows; skip incomplete/invalid ones
 * instead of throwing (publish still uses chaptersFromForm).
 */
export function chaptersFromFormLenient(rows) {
  const parsed = [];
  for (const row of rows ?? []) {
    if (!row.timestamp?.trim() || !row.title?.trim()) continue;
    try {
      const startSeconds = parseTimestampInput(row.timestamp, 'Marca de tiempo');
      parsed.push({ startSeconds, title: row.title.trim() });
    } catch {
      // Incomplete or invalid draft chapter — skip
    }
  }
  return parsed.sort((a, b) => a.startSeconds - b.startSeconds);
}

export function chaptersToForm(chapters) {
  return (chapters ?? []).map((chapter) => ({
    timestamp: formatTimestamp(chapter.startSeconds),
    title: chapter.title,
  }));
}

/** Index of the chapter at or before currentTime, or -1 if before the first chapter. */
export function getActiveChapterIndex(chapters, currentTime = 0) {
  if (!chapters?.length) return -1;
  let index = -1;
  for (let i = 0; i < chapters.length; i += 1) {
    if (chapters[i].startSeconds <= currentTime) index = i;
    else break;
  }
  return index;
}
